import type { TeamWith } from "@redotech/account-common/base-team";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { PickSessionItem } from "@redotech/redo-fulfillment-wms-common/schema/pick-session";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { LocationId } from "@redotech/redo-inventory-id";
import { ProductId } from "@redotech/redo-product-id";
import { StorageLocationId } from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useEffect, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { QuantityIncrementDecrement } from "../shared/quantity-increment-decrement";
import * as styles from "./tote-scanning-page.module.css";
import {
  getPickTitle,
  isItemMatch,
  playErrorSound,
  playSuccessSound,
} from "./util";

interface BinOption {
  storageLocationId: StorageLocationId;
  binName: string;
  availableQuantity: number;
  lastCountedAt?: Date | null;
  isReceive?: boolean;
}

const RECEIVE_SENTINEL_ID = "sl_RECEIVE" as StorageLocationId;

interface BinQuantityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    quantity: number,
    sourceStorageLocationId?: StorageLocationId,
    receiveFromExternal?: boolean,
  ) => void;
  onConfirmLoad: LoadState<void>;
  item: PickSessionItem | FulfillmentOrderLineItem;
  quantityLeftToPick: number;
  team?: TeamWith<{ outboundLabels: OutboundLabelSettings }> | null;
  scanValue: string | null;
  resetScanValue: () => void;
  requireSourceBin?: boolean;
  locationId?: LocationId;
  productId?: ProductId;
}

export const BinQuantityModal = memo(function BinQuantityModal({
  isOpen,
  onClose,
  onConfirm,
  item,
  quantityLeftToPick,
  team,
  scanValue,
  onConfirmLoad,
  resetScanValue,
  requireSourceBin,
  locationId,
  productId,
}: BinQuantityModalProps) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [selectedQuantity, setSelectedQuantity] = useState(
    Math.min(1, quantityLeftToPick),
  );
  const screenType = useScreenType();
  const isMobile = screenType === ScreenType.MOBILE;

  const [selectedBin, setSelectedBin] =
    useState<RedoListItem<BinOption> | null>(null);
  const [binSearchString, setBinSearchString] = useState("");

  const binsLoad = useLoad(
    async (signal) => {
      if (!requireSourceBin || !locationId || !productId) {
        return undefined;
      }
      const result = await rpcClient.getBinsForProduct(
        { locationId, productId, includeStorage: true, includeUncounted: true },
        { signal },
      );
      return {
        bins: result.bins,
        defaultStorageLocationId: result.defaultStorageLocationId,
      };
    },
    [rpcClient, requireSourceBin, locationId, productId],
  );

  const binOptions: RedoListItem<BinOption>[] = useMemo(() => {
    const receiveOption: RedoListItem<BinOption> = {
      type: "text" as const,
      id: RECEIVE_SENTINEL_ID,
      value: {
        storageLocationId: RECEIVE_SENTINEL_ID,
        binName: "Receive",
        availableQuantity: Infinity,
        isReceive: true,
      },
      text: t("Receive"),
    };

    if (!binsLoad.value?.bins) {
      return [receiveOption];
    }
    const bins: BinOption[] = binsLoad.value.bins;
    const filtered = binSearchString
      ? bins.filter((bin) =>
          bin.binName.toLowerCase().includes(binSearchString.toLowerCase()),
        )
      : binsLoad.value.bins;
    return [
      receiveOption,
      ...filtered.map((bin) => ({
        type: "text" as const,
        id: bin.storageLocationId,
        value: bin,
        text: `${bin.binName} (${bin.lastCountedAt == null ? `${bin.availableQuantity} available (uncounted)` : `${bin.availableQuantity} available`})`,
      })),
    ];
  }, [binsLoad.value, binSearchString, t]);

  const selectedBinIsReceive = selectedBin?.value.isReceive === true;

  const selectedBinIsUncounted =
    selectedBin && !selectedBin.value.lastCountedAt && !selectedBinIsReceive;

  const maxQuantity =
    requireSourceBin && selectedBin && !selectedBinIsUncounted
      ? selectedBin.value.isReceive
        ? quantityLeftToPick
        : Math.min(quantityLeftToPick, selectedBin.value.availableQuantity)
      : quantityLeftToPick;

  const handleBinSelected = useHandler(
    ({ item }: { item: RedoListItem<BinOption> }) => {
      setSelectedBin(item);
      if (item.value.isReceive) {
        return;
      }
      if (item.value.lastCountedAt) {
        const newMax = Math.min(
          quantityLeftToPick,
          item.value.availableQuantity,
        );
        if (selectedQuantity > newMax) {
          setSelectedQuantity(Math.max(1, newMax));
        }
      }
    },
  );

  const handleBarcodeScanned = useHandler(async (scannedValue: string) => {
    if (isItemMatch(item, scannedValue, team)) {
      if (selectedQuantity >= maxQuantity) {
        toast(`Cannot pick more than ${maxQuantity} items`, {
          variant: "error",
        });
        void playErrorSound();
      } else {
        const newQuantity = Math.min(selectedQuantity + 1, maxQuantity);
        if (newQuantity === maxQuantity) {
          void playSuccessSound();
          const isReceive = selectedBin?.value.isReceive || false;
          const sourceId = isReceive
            ? undefined
            : selectedBinIsUncounted
              ? (binsLoad.value?.defaultStorageLocationId ?? undefined)
              : selectedBin?.value.storageLocationId;
          onConfirm(newQuantity, sourceId, isReceive);
        } else {
          setSelectedQuantity(newQuantity);
          void playSuccessSound();
        }
      }
    } else {
      toast(`Item does not match scanned value: ${scannedValue}`, {
        variant: "error",
      });
      void playErrorSound();
    }
    resetScanValue();
  });

  useEffect(() => {
    if (scanValue) {
      void handleBarcodeScanned(scanValue);
    }
  }, [scanValue, handleBarcodeScanned]);

  const confirmDisabled =
    selectedQuantity <= 0 || (requireSourceBin && !selectedBin);

  return (
    <RedoBaseModal
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      otherClasses={[styles.modalContentCompact]}
    >
      <Flex dir="column" gap="none" p={isMobile ? "xl" : "3xl"} w="full">
        <Flex align="center" justify="space-between" w="full">
          <Flex dir="column" gap="xs">
            <Text fontSize={isMobile ? "md" : "lg"} fontWeight="semibold">
              {t("Confirm quantity ({{selected}}/{{total}})", {
                selected: selectedQuantity,
                total: quantityLeftToPick,
              })}
            </Text>
            {!isMobile && (
              <Text fontSize="sm" textColor="secondary">
                {t("Scan each item to pick, or adjust quantity manually.")}
              </Text>
            )}
          </Flex>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size={isMobile ? "md" : "xl"}
          />
        </Flex>

        <Flex align="center" gap="md" py={isMobile ? "sm" : "3xl"} w="full">
          {item.imageUrl ? (
            <img
              alt={`Product ${item.sku}`}
              className={classNames(
                styles.productImage,
                isMobile && styles.productImageCompact,
              )}
              src={item.imageUrl}
            />
          ) : (
            <div
              className={classNames(
                styles.productImagePlaceholder,
                isMobile && styles.productImagePlaceholderCompact,
              )}
            />
          )}
          <Flex dir="column" gap="xxs" grow="1">
            {"bin" in item && item.bin && (
              <Text fontSize={isMobile ? "md" : "lg"} fontWeight="bold">
                {t("Bin:")} {item.bin}
              </Text>
            )}
            <Text fontSize={isMobile ? "xs" : "sm"} textColor="secondary">
              {getPickTitle(item)}
            </Text>
            {item.sku && (
              <Text fontSize="xs" textColor="tertiary">
                {t("SKU:")} {item.sku}
              </Text>
            )}
            {item.customAttributes.find((ca) =>
              ca.key.toLowerCase().includes("brand style"),
            ) && (
              <Text fontSize="xs" textColor="tertiary">
                {t("Brand style:")}{" "}
                {
                  item.customAttributes.find((ca) =>
                    ca.key.toLowerCase().includes("brand style"),
                  )?.value
                }
              </Text>
            )}
          </Flex>
        </Flex>

        <Divider classes={styles.lightDivider} />

        {requireSourceBin && (
          <Flex dir="column" gap="sm" py="md" w="full">
            <RedoSingleSelectDropdownInput
              emptyListMessage={t("No bins found")}
              itemsLoading={binsLoad.pending}
              label={t("Source bin")}
              options={binOptions}
              optionSelected={handleBinSelected}
              placeholder={t("Select source bin...")}
              searchPlaceholder={t("Search bins...")}
              searchString={binSearchString}
              selectedItem={selectedBin ?? undefined}
              setSearchString={setBinSearchString}
              size="md"
            />
          </Flex>
        )}

        <QuantityIncrementDecrement
          compact={isMobile}
          max={maxQuantity}
          min={0}
          setValue={setSelectedQuantity}
          subtitle={t("Remaining: {{count}}", {
            count: quantityLeftToPick - selectedQuantity,
          })}
          title={t("Quantity")}
          value={selectedQuantity}
        />

        <Flex dir="column" gap={isMobile ? "sm" : "md"} w="full">
          <RedoButton
            className="w-full"
            disabled={confirmDisabled}
            hierarchy="primary"
            onClick={() => {
              const isReceive = selectedBin?.value.isReceive || false;
              const sourceId = isReceive
                ? undefined
                : selectedBinIsUncounted
                  ? (binsLoad.value?.defaultStorageLocationId ?? undefined)
                  : selectedBin?.value.storageLocationId;
              onConfirm(selectedQuantity, sourceId, isReceive);
            }}
            pending={onConfirmLoad.pending}
            size={isMobile ? "md" : "xl"}
            style={
              !confirmDisabled
                ? { backgroundColor: "#f54a00", outlineColor: "#f54a00" }
                : undefined
            }
            text={t("Confirm")}
            theme="normal"
          />
          <RedoButton
            className="w-full"
            hierarchy="secondary"
            onClick={onClose}
            size={isMobile ? "md" : "xl"}
            text={t("Cancel")}
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
